import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The dismiss behaviour the header's two dropdowns share — the account menu
 * and the notifications panel.
 *
 * CONTROLLED, NOT SELF-OWNED
 *
 * The hook takes `isOpen` and `onClose` rather than holding the state itself,
 * because the header has to guarantee that only one of the two is down at a
 * time. Two panels each owning their own boolean cannot make that promise, and
 * the outside-click test below is no help: a click inside the account menu is,
 * correctly, not outside it, so the bell would stay open underneath. With the
 * state in `StudentHeader` opening one simply sets the other's `isOpen` false.
 *
 * What it does own is the closing, in the four ways both panels need:
 *
 *  - a click outside the trigger and the panel;
 *  - Escape, with focus handed back to the trigger, so a keyboard reader is
 *    not dropped at the top of the document;
 *  - following a link inside the panel, which is a route change;
 *  - unmounting.
 *
 * `pointerdown` rather than `click` for the outside test. A click fires after
 * the button under the pointer has run its own handler, so closing on click
 * would shut a menu and let the same gesture's toggle reopen it.
 */
const useDismissable = ({ isOpen, onClose }) => {
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const { pathname } = useLocation();

    // Kept in a ref so the listener effect below depends on `isOpen` alone. A
    // caller that passes a fresh arrow each render would otherwise tear the
    // listeners down and put them back on every keystroke elsewhere on the page.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; });

    // Leaving a panel open across a navigation would cover the page it lands on.
    useEffect(() => {
        if (isOpen) onCloseRef.current?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onPointerDown = (event) => {
            const insideTrigger = triggerRef.current?.contains(event.target);
            const insidePanel = panelRef.current?.contains(event.target);
            if (!insideTrigger && !insidePanel) onCloseRef.current?.();
        };

        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            onCloseRef.current?.();
            triggerRef.current?.focus();
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    return { triggerRef, panelRef };
};

export default useDismissable;
